/**
 * The landing page's figures (`/dashboard`, `src/pages/dashboard/Analytics.tsx`): the read, and the
 * arithmetic over it, kept out of the component so a test can run both against a server that caps.
 *
 * THE READ WAS CUT TO A SEVENTH OF THE YEAR AND THE SCREEN GAVE NO SIGN. It asked for every
 * transaction in the chosen months in ONE request, and PostgREST's Max rows (1,000) cut that request
 * without an error. On the default twelve months (6,957 rows on 2026-10-17) only the oldest 1,000
 * arrived, ending 2026-01-08: revenue showed GHS 2,597,969.34 of 16,803,501.28, expenses
 * 228,889.08 of 9,192,955.04, the USD lines vanished, and the trend chart simply ended in January.
 * It now reads every page and throws unless the server's own count is met (`fetchAllRows`).
 */

import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { businessDay } from '@/lib/format';
import { fetchAllRows } from '@/lib/fetchAllRows';

/**
 * The five columns the figures read, and the `id` the paged read checks. `select('*')` carried all
 * ten (fees, references, audit times): 2,341,225 bytes of row JSON for the default year (6,957 rows
 * on 2026-10-17), against 1,454,140 for these six. The `id` costs 81,006 of those bytes, about 12 a
 * row (1,373,134 without it), and buys the duplicate-row check: a transaction re-dated while the
 * seven pages are read moves across a page boundary without changing the count, and only its key
 * shows it arriving twice.
 */
export const LANDING_TRANSACTION_COLUMNS = 'id, amount, type, currency, description, transaction_at';

export type LandingTransaction = Pick<
  Tables<'transactions'>,
  'id' | 'amount' | 'type' | 'currency' | 'description' | 'transaction_at'
>;

/**
 * Every transaction from `fromDate` (inclusive) to `toDateExclusive`, oldest first, in pages.
 *
 * ORDERED BY `transaction_at` THEN `id`, AND THE `id` IS REQUIRED. Paging by offset is only safe on
 * a total order, and `transaction_at` is not unique: in the default year 253 timestamps carry more
 * than one row and one carries 88. Most are 00:00, rows recorded with a date and no time (937 of
 * the 1,144 tied rows). Rows that tie come back in whatever order Postgres finds them, which can
 * differ between two requests, so a tie straddling a page boundary could put one row on both pages
 * and its neighbour on neither, and the row count would still add up. `id` is the primary key, so `(transaction_at, id)` orders every row
 * one way on every request: the D-T28 tiebreaker, ascending here because the read is. Oldest first
 * is kept because the trend chart draws its months in the order they first appear.
 */
export function fetchLandingTransactions(
  fromDate: string,
  toDateExclusive: string,
): Promise<LandingTransaction[]> {
  return fetchAllRows({
    label: 'transactions',
    orderedBy: 'transaction_at, id',
    page: (from, to) => supabase
      .from('transactions')
      .select(LANDING_TRANSACTION_COLUMNS, { count: 'exact' })
      .gte('transaction_at', fromDate)
      .lt('transaction_at', toDateExclusive)
      .order('transaction_at', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to),
    keyOf: (tx) => tx.id,
  });
}

/**
 * Revenue, expenses, net income, the monthly trend and the top-8 breakdowns, per currency.
 * Moved unchanged from the page's `useMemo`.
 */
export function summariseLandingTransactions(transactions: readonly LandingTransaction[] | undefined) {
  if (!transactions) {
    return { stats: null, monthlyTrend: [], revenueBreakdown: [], expenseBreakdown: [], otherCurrencies: [] };
  }

  let totalRevenue = 0;
  let totalExpenses = 0;
  const revenueByCategory = new Map<string, number>();
  const expenseByCategory = new Map<string, number>();
  const monthlyData = new Map<string, { revenue: number; expenses: number }>();
  const otherByCurrency = new Map<string, {
    revenue: number;
    expenses: number;
    revenueByCategory: Map<string, number>;
    expenseByCategory: Map<string, number>;
  }>();

  transactions.forEach(tx => {
    // ONE SUM PER CURRENCY. The table holds GHS and USD lines, and a USD amount added to a cedi
    // amount is a number in neither currency. Everything below the guard is cedis, and the chart and
    // pies draw cedis only, because one axis cannot carry two currencies; any other currency is
    // totalled here on its own and printed beside the cedi figures, never folded into them.
    if (tx.currency !== 'GHS') {
      if (!otherByCurrency.has(tx.currency)) {
        otherByCurrency.set(tx.currency, { revenue: 0, expenses: 0, revenueByCategory: new Map(), expenseByCategory: new Map() });
      }
      const other = otherByCurrency.get(tx.currency)!;
      const category = tx.description || 'Other';
      if (tx.type === 'CREDIT') {
        other.revenue += tx.amount;
        other.revenueByCategory.set(category, (other.revenueByCategory.get(category) || 0) + tx.amount);
      } else if (tx.type === 'DEBIT') {
        const amount = Math.abs(tx.amount);
        other.expenses += amount;
        other.expenseByCategory.set(category, (other.expenseByCategory.get(category) || 0) + amount);
      }
      return;
    }

    // The BUCKET key, not just its label: read in local parts, a 23:30 Ghana payment on the last day
    // of a month fell into the next month's revenue from any positive-offset browser.
    const month = tx.transaction_at ? format(businessDay(tx.transaction_at)!, 'MMM yyyy') : 'Unknown';

    if (!monthlyData.has(month)) {
      monthlyData.set(month, { revenue: 0, expenses: 0 });
    }
    const monthStats = monthlyData.get(month)!;

    if (tx.type === 'CREDIT') {
      totalRevenue += tx.amount;
      monthStats.revenue += tx.amount;
      const category = tx.description || 'Other';
      revenueByCategory.set(category, (revenueByCategory.get(category) || 0) + tx.amount);
    } else if (tx.type === 'DEBIT') {
      const amount = Math.abs(tx.amount);
      totalExpenses += amount;
      monthStats.expenses += amount;
      const category = tx.description || 'Other';
      expenseByCategory.set(category, (expenseByCategory.get(category) || 0) + amount);
    }
  });

  const topEight = (byCategory: Map<string, number>) => Array.from(byCategory.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  return {
    stats: {
      periodRevenue: totalRevenue,
      periodExpenses: totalExpenses,
      netIncome: totalRevenue - totalExpenses,
    },
    monthlyTrend: Array.from(monthlyData.entries()).map(([month, data]) => ({
      month,
      revenue: data.revenue,
      expenses: data.expenses,
    })),
    revenueBreakdown: Array.from(revenueByCategory.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8),
    expenseBreakdown: Array.from(expenseByCategory.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8),
    otherCurrencies: Array.from(otherByCurrency.entries()).map(([currency, other]) => ({
      currency,
      revenue: other.revenue,
      expenses: other.expenses,
      netIncome: other.revenue - other.expenses,
      revenueBreakdown: topEight(other.revenueByCategory),
      expenseBreakdown: topEight(other.expenseByCategory),
    })),
  };
}
